import type { ActionButton } from '@app/src/components/ActionButtons'
import ActionButtons from '@app/src/components/ActionButtons'
import LoadingButton from '@app/src/components/LoadingButton'
import { useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { ArrowBack, ArrowForward } from '@mui/icons-material'
import type { ButtonProps } from '@mui/material'
import { Alert, Box, Button, Stack, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/styles'
import type { ReactNode } from 'react'
import React, { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import CreationModalStepSplitView from './CreationModalStepSplitView'

export type CreationModalStepProps = {
  title: string
  fieldnames: Array<string>
  description?: ReactNode
  hasSkipButton?: boolean
  children: ReactNode
  onBack?: () => void
  disableInitialBack?: boolean
  overrideButtonTextId?: string
  hideNextButton?: boolean
  isLoading?: boolean
  widthOverride?: number
  extraButtons?: Array<ActionButton>
  nextButtonProps?: ButtonProps
  informationAlert?: ReactNode
  splitView?: {
    enabled: boolean
    component: ReactNode
  }
  canGoNext?: (values: unknown) => boolean
  nextButtonTooltip?: string
  customOnClickNext?: () => Promise<void>
  customOnClickSkip?: () => void
  inlineButtons?: boolean
  footerContent?: ReactNode
}

const CreationModalStep: React.FC<CreationModalStepProps> = ({
  title,
  fieldnames,
  description,
  children,
  hasSkipButton,
  onBack,
  disableInitialBack = false,
  overrideButtonTextId,
  isLoading = false,
  widthOverride,
  hideNextButton,
  extraButtons,
  nextButtonProps,
  informationAlert,
  splitView,
  canGoNext: canGoNextOverride,
  nextButtonTooltip,
  customOnClickNext,
  customOnClickSkip,
  inlineButtons = false,
  footerContent,
}) => {
  const { activeStep, totalSteps, setActiveStep, enableEmptyState, allowsNextWithoutData } = useCreationModalProgress()
  const { formatMessage } = useIntl()
  const { setValue, watch, getValues } = useFormContext()
  const theme = useTheme()

  const canGoPrev = useMemo(() => activeStep > 1, [activeStep])
  const isLastStep = useMemo(() => activeStep === totalSteps, [activeStep, totalSteps])
  const border = `1px solid ${theme.palette.divider}`

  const handleBack = () => {
    if (canGoPrev) {
      setActiveStep(currentValue => currentValue - 1)
    } else {
      onBack?.()
    }
  }

  const fieldValuesWatch = fieldnames.map(field => watch(field))

  const canGoNext = useMemo(() => {
    if (canGoNextOverride) return canGoNextOverride(getValues())
    if (allowsNextWithoutData || hideNextButton) {
      return true
    }
    const hasNextStep = activeStep < totalSteps + 1
    const hasValue = fieldValuesWatch.every(fieldValue =>
      Array.isArray(fieldValue)
        ? Boolean(fieldValue.length)
        : typeof fieldValue === 'number'
          ? Number.isFinite(fieldValue)
          : typeof fieldValue === 'boolean'
            ? true // Boolean fields are always valid (can be true or false)
            : Boolean(fieldValue),
    )

    return hasNextStep && hasValue
  }, [activeStep, totalSteps, fieldValuesWatch, allowsNextWithoutData, canGoNextOverride])

  const hasAnyFooterButtons = useMemo(() => {
    if (inlineButtons) return true // Always show buttons when inline
    const hasBackButton = !(disableInitialBack && activeStep === 1)
    const hasNextOrSubmitButton = isLastStep || (!enableEmptyState && !hideNextButton)
    return hasBackButton || hasNextOrSubmitButton || hasSkipButton || (extraButtons && extraButtons.length > 0)
  }, [
    disableInitialBack,
    activeStep,
    isLastStep,
    enableEmptyState,
    hideNextButton,
    hasSkipButton,
    extraButtons,
    inlineButtons,
  ])

  if (splitView?.enabled && splitView.component) {
    return (
      <CreationModalStepSplitView
        title={title}
        fieldnames={fieldnames}
        description={description}
        children={children}
        hasSkipButton={hasSkipButton}
        disableInitialBack={disableInitialBack}
        overrideButtonTextId={overrideButtonTextId}
        isLoading={isLoading}
        hideNextButton={hideNextButton}
        extraButtons={extraButtons}
        nextButtonProps={nextButtonProps}
        splitView={splitView}
        canGoNext={canGoNext}
        handleBack={handleBack}
        isLastStep={isLastStep}
      />
    )
  }

  return (
    <Box display="flex" flexDirection="column" width="100%" height="100%">
      <Box flex={1} display="flex" justifyContent="center" overflow={inlineButtons ? 'auto' : undefined}>
        <Box width={widthOverride ?? 780} px={3} py={6} pb={inlineButtons ? 6 : 12}>
          {!enableEmptyState && (
            <>
              {(title || description) && (
                <Stack spacing={2} pb={4}>
                  <Typography variant="h1" gutterBottom>
                    {title}
                  </Typography>
                  <Typography variant="body1" color="textSecondary" component="span">
                    {description}
                  </Typography>
                </Stack>
              )}
            </>
          )}
          <Box>{children}</Box>
        </Box>
      </Box>
      {!inlineButtons && hasAnyFooterButtons && (
        <Box
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          zIndex={1300}
          bgcolor="background.paper"
          py={2}
          borderTop={border}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            maxWidth={widthOverride ?? 780}
            mx="auto"
            px={3}
          >
            {disableInitialBack && activeStep === 1 ? (
              <Box />
            ) : (
              <Button onClick={handleBack} startIcon={<ArrowBack />}>
                {formatMessage({ id: 'general.back' })}
              </Button>
            )}

            {isLastStep ? (
              <Box display="flex" gap={1} alignItems="center">
                {footerContent && <Box mr={2}>{footerContent}</Box>}
                {extraButtons && <ActionButtons buttons={extraButtons} />}
                <LoadingButton
                  loading={isLoading}
                  variant="contained"
                  type="submit"
                  disabled={!canGoNext}
                  {...nextButtonProps}
                >
                  {formatMessage({ id: overrideButtonTextId ?? `form.createRequest.submit` })}
                </LoadingButton>
              </Box>
            ) : (
              <Box display="flex" gap={1} alignItems="center">
                {footerContent && <Box mr={2}>{footerContent}</Box>}
                {hasSkipButton && (
                  <Box mr={1}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        if (customOnClickSkip) {
                          customOnClickSkip()
                        } else {
                          fieldnames.forEach(fieldName => {
                            setValue(fieldName, undefined)
                          })
                          setActiveStep(currentValue => currentValue + 1)
                        }
                      }}
                    >
                      {formatMessage({ id: 'general.skip' })}
                    </Button>
                  </Box>
                )}

                {extraButtons && <ActionButtons buttons={extraButtons} />}

                {!enableEmptyState && !hideNextButton && (
                  <Tooltip title={nextButtonTooltip} disableHoverListener={!nextButtonTooltip}>
                    <Box>
                      <LoadingButton
                        loading={isLoading}
                        variant="contained"
                        endIcon={<ArrowForward />}
                        disabled={!canGoNext}
                        onClick={async () => {
                          await customOnClickNext?.()
                          setActiveStep(currentValue => currentValue + 1)
                        }}
                        data-testid="continue-button"
                        {...nextButtonProps}
                      >
                        {formatMessage({
                          id: overrideButtonTextId ?? 'general.next',
                        })}
                      </LoadingButton>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
      {!inlineButtons && informationAlert && (
        <Box
          position="fixed"
          bottom={hasAnyFooterButtons ? 80 : 0}
          left={0}
          right={0}
          zIndex={1300}
          py={2}
          borderTop={1}
          borderColor="divider"
          bgcolor="background.paper"
        >
          <Box maxWidth={widthOverride ?? 780} mx="auto" px={2}>
            <Alert variant="filled" severity="info" sx={{ backgroundColor: 'common.white', alignItems: 'flex-start' }}>
              {informationAlert}
            </Alert>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default CreationModalStep
